import { Box, Tooltip, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'

// ==================== Constants ====================
// Row bar height in px.
const BAR_HEIGHT = 24
// Visibility floor: the smallest rendered width (px) a non-zero segment may
// shrink to, so a thin sliver stays visible instead of disappearing. Labels are
// not considered here — they are shown only when they fit (see labelFits). Zero
// segments are not rendered at all.
const MIN_SEGMENT_WIDTH = 4
// Horizontal breathing room (px) required around a label for it to count as
// fitting inside its segment.
const LABEL_PADDING = 8
// Font string used for canvas text measurement. Matches the rendered label
// style (fontWeight 700 at the xs font size). measureText cannot resolve CSS
// variables, so --font-size-xs (12px in styles/variables.css) is inlined.
const LABEL_FONT = '700 12px sans-serif'
// Smallest fraction of the track width given to the shortest-ranked bar (rank
// 1), so even the smallest-total row stays clearly visible. The top-ranked row
// gets the full track (fraction 1). Ranks spread evenly across this range.
const MIN_BAR_FRACTION = 0.25
// Max tooltip panel width (px). Caps the dark hover tooltip so a long row label
// wraps onto multiple lines instead of stretching the panel across the card.
const TOOLTIP_MAX_WIDTH = 280

// ==================== Helpers ====================
// Cached singleton 2D canvas context used for text measurement. Created lazily
// on first use and reused across all calls so no extra DOM nodes or reflows are
// introduced per segment.
let measureContext
// Measure the rendered pixel width of `text` at the given `font` string using a
// cached canvas 2D context. Returns 0 for empty/nullish text. When a canvas
// context is unavailable (SSR/test environment with no document, or
// getContext('2d') returns null), falls back to a conservative per-character
// estimate so callers never throw.
const measureTextWidth = (text, font) => {
  if (!text) return 0
  // Lazily create and cache the measurement context once.
  if (measureContext === undefined) {
    measureContext =
      typeof document !== 'undefined'
        ? document.createElement('canvas').getContext('2d')
        : null
  }
  // Fallback when no canvas context is available: approximate each character at
  // ~0.6em of the px size parsed from the font string.
  if (!measureContext) {
    const parsedSize = parseInt(font, 10)
    const charWidth = Number.isNaN(parsedSize) ? 7 : parsedSize * 0.6
    return text.length * charWidth
  }
  // Measure with the real canvas context.
  measureContext.font = font
  return measureContext.measureText(text).width
}

// Decide whether a label `value` fits inside a segment of the given pixel
// `width`. A label fits when its measured width plus LABEL_PADDING breathing
// room is no wider than the segment. Returns false for a non-positive width or
// an empty/nullish value so a hidden/empty segment never claims a fit.
const labelFits = (value, width) => {
  if (width <= 0 || !value) return false
  return measureTextWidth(value, LABEL_FONT) + LABEL_PADDING <= width
}

// Split a bar of `totalWidth` px (already scaled to the row's share of the
// largest total by the caller) into its two segments in proportion to their
// values RELATIVE TO EACH OTHER. A non-zero segment is floored to
// MIN_SEGMENT_WIDTH so a thin sliver stays visible, and the other segment gives
// up the space. A zero segment gets 0 width (hidden) and the non-zero one fills
// the whole bar. Segments are laid out sequentially — no overlap.
const computeSegmentWidths = (expenseValue, whtValue, totalWidth) => {
  const expense = expenseValue > 0 ? expenseValue : 0
  const wht = whtValue > 0 ? whtValue : 0
  const total = expense + wht
  if (totalWidth <= 0 || total <= 0) {
    return { expenseWidth: 0, whtWidth: 0 }
  }
  // Only one side has a value → it fills the track.
  if (expense === 0) return { expenseWidth: 0, whtWidth: totalWidth }
  if (wht === 0) return { expenseWidth: totalWidth, whtWidth: 0 }
  // Both present → proportional split, then enforce the per-segment minimum
  // without exceeding the track width.
  let expenseWidth = (expense / total) * totalWidth
  let whtWidth = totalWidth - expenseWidth
  if (expenseWidth < MIN_SEGMENT_WIDTH) {
    expenseWidth = Math.min(MIN_SEGMENT_WIDTH, totalWidth - MIN_SEGMENT_WIDTH)
    whtWidth = totalWidth - expenseWidth
  } else if (whtWidth < MIN_SEGMENT_WIDTH) {
    whtWidth = Math.min(MIN_SEGMENT_WIDTH, totalWidth - MIN_SEGMENT_WIDTH)
    expenseWidth = totalWidth - whtWidth
  }
  return { expenseWidth, whtWidth }
}

// A single colored segment with an optional centered value label. Renders
// nothing when width is 0 (e.g. a zero WHT value). The label is rendered only
// when showLabel is true (it fits inside the segment); otherwise the colored
// block is rendered with no text. overflow: 'hidden' is kept as a safety net.
const Segment = ({ width, color, value, showLabel }) => {
  if (width <= 0) {
    return null
  }
  return (
    <Box
      sx={{
        width: `${width}px`,
        height: `${BAR_HEIGHT}px`,
        backgroundColor: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {showLabel && (
        <Typography
          sx={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            color: 'var(--forbrand-primary)',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  )
}

// Dark hover-tooltip body for a row, styled to match VarianceTooltip in
// MonthlyWHTTrend.jsx (dark background, white text, color swatches,
// right-aligned values). Shows the row label as a title, then one row per
// legend entry (legend[0] = expense, legend[1] = wht) with a color swatch, the
// legend label, and the formatted value (expenseDisplay / whtDisplay).
const RowTooltipContent = ({ item, legend }) => {
  const values = [item.expenseDisplay, item.whtDisplay]
  return (
    <Box
      sx={{
        backgroundColor: 'var(--forbrand-primary)',
        borderRadius: '4px',
        p: 1.5,
        minWidth: 200,
        maxWidth: TOOLTIP_MAX_WIDTH,
      }}
    >
      <Typography
        sx={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 700,
          color: 'var(--white-color)',
          pb: 0.5,
          // Long titles wrap onto multiple lines instead of stretching the
          // tooltip across the card.
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
        }}
      >
        {item.label}
      </Typography>

      {legend.map((entry, index) => (
        <Box
          key={entry.label}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                backgroundColor: entry.color,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--white-color)',
              }}
            >
              {entry.label}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--white-color)',
              textAlign: 'right',
            }}
          >
            {values[index]}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

// The row total used to size and order bars. Non-positive values contribute 0.
const rowTotal = item => {
  const expense = item.expenseValue > 0 ? item.expenseValue : 0
  const wht = item.whtValue > 0 ? item.whtValue : 0
  return expense + wht
}

// Map each row to a bar-length FRACTION (0..1) of the track by its RANK among
// all rows' totals, so any difference in total — however small — changes the
// rank and therefore the bar length (no two unequal totals tie). Returns a Map
// of item.label -> fraction. Dense ranking: equal totals share a rank; distinct
// totals step by one. Fractions spread evenly across [MIN_BAR_FRACTION, 1], with
// the largest total at 1. Rows with a zero total get fraction 0 (no bar).
const buildRankedFractionByLabel = items => {
  const fractionByLabel = new Map()
  const nonZero = items.filter(item => rowTotal(item) > 0)
  if (nonZero.length === 0) return fractionByLabel
  // Ascending sort so the smallest total ranks lowest (shortest bar).
  const sorted = [...nonZero].sort((a, b) => rowTotal(a) - rowTotal(b))
  // Dense ranking: equal totals share a rank; distinct totals step by one.
  let rank = 0
  let previousTotal = null
  const rankByLabel = new Map()
  sorted.forEach(item => {
    const total = rowTotal(item)
    if (previousTotal === null || total !== previousTotal) {
      rank += 1
      previousTotal = total
    }
    rankByLabel.set(item.label, rank)
  })
  const maxRank = rank
  nonZero.forEach(item => {
    // maxRank === 1 → all totals equal; give them the full track.
    const step =
      maxRank > 1 ? (rankByLabel.get(item.label) - 1) / (maxRank - 1) : 1
    const fraction = MIN_BAR_FRACTION + step * (1 - MIN_BAR_FRACTION)
    fractionByLabel.set(item.label, fraction)
  })
  return fractionByLabel
}

// A responsive bar row: measures the full track width via ResizeObserver, scales
// the bar length to barFraction (a rank-based 0..1 share of the track so rows
// are visually ranked by total), then splits that bar into two segments by the
// expense:WHT ratio. Lower-ranked rows leave empty space to the right.
const BarRow = ({ item, legend, barFraction }) => {
  // ==================== Hooks ====================
  const trackRef = useRef(null)
  // ==================== State ====================
  const [trackWidth, setTrackWidth] = useState(0)
  // ==================== Effects ====================
  useEffect(() => {
    const node = trackRef.current
    if (!node) return undefined
    setTrackWidth(node.clientWidth)
    const observer = new ResizeObserver(entries => {
      setTrackWidth(entries[0].contentRect.width)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  // ==================== Derived Data ====================
  // Scale this row's bar to its rank-based fraction of the track so bars are
  // visually ranked by total. A zero fraction (zero-total row) collapses the bar.
  const barWidth = trackWidth * barFraction
  // Split the scaled bar into the two segments by the expense:WHT ratio.
  const { expenseWidth, whtWidth } = computeSegmentWidths(
    item.expenseValue,
    item.whtValue,
    barWidth
  )
  const expenseFits = labelFits(item.expenseDisplay, expenseWidth)
  const whtFits = labelFits(item.whtDisplay, whtWidth)
  // ==================== Return ====================
  // The structure is ALWAYS the same: a Tooltip wrapping a single outer Box that
  // owns the ResizeObserver ref. The ref lives on this stable outer wrapper (not
  // on a conditionally-rendered element), so measurement never collapses. The
  // tooltip is always enabled so the full expense / WHT values are available on
  // hover for every row, regardless of whether a label fits inside its segment.
  // The full-width track holds the scaled bar; the remaining space stays empty
  // so shorter-total rows render shorter bars.
  return (
    <Tooltip
      title={<RowTooltipContent item={item} legend={legend} />}
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: 'transparent',
            p: 0,
            maxWidth: TOOLTIP_MAX_WIDTH,
          },
        },
      }}
    >
      {/* Outer Box is the Tooltip's ref target (MUI attaches its own ref here).
          The inner Box owns trackRef for the ResizeObserver, so MUI never
          hijacks the measurement ref. */}
      <Box sx={{ width: '100%' }}>
        <Box
          ref={trackRef}
          sx={{ display: 'flex', width: '100%', height: `${BAR_HEIGHT}px` }}
        >
          <Segment
            width={expenseWidth}
            color={legend[0].color}
            value={item.expenseDisplay}
            showLabel={expenseFits}
          />
          <Segment
            width={whtWidth}
            color={legend[1].color}
            value={item.whtDisplay}
            showLabel={whtFits}
          />
        </Box>
      </Box>
    </Tooltip>
  )
}

const HorizontalGraph = ({ heading, legend, note, items }) => {
  // ==================== Derived Data ====================
  // Rows render in the ORIGINAL data order (no reordering). Only the bar LENGTH
  // is rank-based: build each row's rank-based fraction so bar length reflects
  // where its total ranks, independent of render order.
  const fractionByLabel = buildRankedFractionByLabel(items)
  // ==================== Return ====================
  return (
    <Box
      sx={{
        border: '1px solid var(--border-subtel)',
        backgroundColor: 'var(--white-color)',
        p: 2,
        flex: 1,
        minWidth: 300,
        minHeight: 500,
      }}
    >
      {/* Header */}
      <Typography
        sx={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 500,
          color: 'var(--forbrand-primary)',
          mb: 1,
        }}
      >
        {heading}
      </Typography>

      {/* Legend + Note */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {legend.map(item => (
            <Box
              key={item.label}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  backgroundColor: item.color,
                  borderRadius: '2px',
                }}
              />
              <Typography
                sx={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--forbrand-primary)',
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
        {note && (
          <Typography
            sx={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--forbrand-primary)',
              textAlign: 'center',
            }}
          >
            {note}
          </Typography>
        )}
      </Box>

      {/* Bar Items — original data order; bar length is rank-based */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.length ? (
          items.map(item => (
            <Box key={item.label}>
              <Typography
                sx={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--forbrand-primary)',
                  mb: 0.5,
                  lineHeight: '14px',
                }}
              >
                {item.label}
              </Typography>
              <BarRow
                item={item}
                legend={legend}
                barFraction={fractionByLabel.get(item.label) || 0}
              />
            </Box>
          ))
        ) : (
          <Typography
            sx={{
              color: 'var(--forbrand-primary)',
              fontSize: 'var(--font-size-md)',
            }}
          >
            No record found
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default HorizontalGraph
